import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { AdminProvider, useAdmin } from './context/AdminContext';
import Login from './components/Login';
import Dashboard from './components/Dashboard';
import UserDetailPage from './components/UserDetailPage';
import GrowthPage from './components/GrowthPage';
import TeamPage from './components/TeamPage';
import EnterpriseInquiriesPage from './components/EnterpriseInquiriesPage';
import { ADMIN_PAGES } from './components/AdminNav';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 60 * 1000,
      refetchOnWindowFocus: false,
      retry: 1,
    },
  },
});

// Protected Route Component to enforce session checks, and (optionally) access to one page.
// Without access, send the admin to the first page they can open instead of an empty screen.
function ProtectedRoute({ children, page }) {
  const { admin, can, isOwner } = useAdmin();
  if (!admin) {
    return <Navigate to="/login" replace />;
  }
  const rule = page && ADMIN_PAGES.find((p) => p.path === page);
  if (rule && !rule.allowed({ can, isOwner })) {
    const fallback = ADMIN_PAGES.find((p) => p.allowed({ can, isOwner }));
    return fallback ? <Navigate to={fallback.path} replace /> : <NoAccess />;
  }
  return children;
}

function NoAccess() {
  const { logout } = useAdmin();
  return (
    <div className="min-h-screen flex items-center justify-center p-6">
      <div className="glass-panel rounded-2xl p-6 border border-white/5 max-w-sm text-center space-y-3">
        <h1 className="text-lg font-black text-white">No pages assigned</h1>
        <p className="text-xs text-slate-400">Your account doesn't have access to any page yet. Ask the account owner to give you access.</p>
        <button onClick={logout} className="px-4 py-2 rounded-xl text-xs font-bold bg-indigo-500 hover:bg-indigo-400 text-white">Sign out</button>
      </div>
    </div>
  );
}

// Public Route Component to prevent logged-in admins from accessing /login
function PublicRoute({ children }) {
  const { admin } = useAdmin();
  if (admin) {
    return <Navigate to="/" replace />;
  }
  return children;
}

function AppRoutes() {
  return (
    <div className="min-h-screen bg-[#070a13] text-slate-100 relative select-none">
      <Routes>
        <Route 
          path="/login" 
          element={
            <PublicRoute>
              <Login />
            </PublicRoute>
          } 
        />
        <Route
          path="/"
          element={
            <ProtectedRoute page="/">
              <Dashboard />
            </ProtectedRoute>
          }
        />
        <Route
          path="/user/:userId"
          element={
            <ProtectedRoute page="/">
              <UserDetailPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/growth"
          element={
            <ProtectedRoute page="/growth">
              <GrowthPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/team"
          element={
            <ProtectedRoute page="/team">
              <TeamPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/enterprise"
          element={
            <ProtectedRoute page="/enterprise">
              <EnterpriseInquiriesPage />
            </ProtectedRoute>
          }
        />
        {/* Redirect /user without an ID to the dashboard */}
        <Route 
          path="/user" 
          element={<Navigate to="/" replace />} 
        />
        {/* Default fallback redirects to home */}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </div>
  );
}

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <BrowserRouter>
        <AdminProvider>
          <AppRoutes />
        </AdminProvider>
      </BrowserRouter>
    </QueryClientProvider>
  );
}

export default App;
